import React, { useEffect, useState } from "react";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { Stage, Layer, Image as KonvaImage, Arrow } from "react-konva";
import { XCircleIcon } from "@heroicons/react/24/solid";
import { BASEURL } from "../../../constants";

const IMAGE_WIDTH = 600;
const IMAGE_HEIGHT = 400;

const PreviewCategorySelector = ({ data }) => {
    const { imageKey, annotations = [] } = data || {};
    const [loadedImage, setLoadedImage] = useState(null);
    const [inputs, setInputs] = useState([]);
    const [labels, setLabels] = useState([]);
    const [imageUrl, setImageUrl] = useState("");
    const [isCorrect, setIsCorrect] = useState(null);

    useEffect(() => {
        if (imageKey) {
            fetchImage(imageKey);
        } else {
            setLoadedImage(null);
        }

        setInputs(annotations.map(ann => ({ ...ann, value: "", isCorrect: false })));
        setLabels(annotations.map(ann => ({ text: ann.text, id: ann.id })));
    }, [imageKey, annotations]);

    const fetchImage = async (key) => {
        try {
            const uniqueId = uuidv4();
            const response = await axios.get(`${BASEURL}/api/get-image`, {
                params: { imageId: key },
            });

            if (response.data?.dataUrl) {
                setImageUrl(response.data.dataUrl);
                const img = new window.Image();
                img.src = response.data.dataUrl;
                img.onload = () => setLoadedImage(img);
            }
        } catch (error) {
            console.error("Error fetching image:", error);
        }
    };

    const handleDrop = (e, id) => {
        e.preventDefault();
        const text = e.dataTransfer.getData("text/plain");
    
        const updatedInputs = inputs.map(input => {
            if (input.id === id) {
                return { ...input, value: text, isCorrect: input.text === text };
            }
            return input;
        });
    
        setInputs(updatedInputs);
        setLabels(labels.filter(label => label.text !== text));
    
        // Check if all inputs are correctly placed
        const allCorrect = updatedInputs.every(input => input.value && input.isCorrect);
        if (updatedInputs.every(input => input.value)) {
            setIsCorrect(allCorrect);
        }
    };
    

    const handleRemove = (id, text) => {
        setInputs(inputs.map(input => 
            input.id === id ? { ...input, value: "", isCorrect: false } : input
        ));
        setLabels([...labels, { text, id }]);
        setIsCorrect(null);
    };

    return (
        <div className="font-sans flex flex-col items-center bg-gray-100 p-6">
            <h2 className="text-2xl font-semibold mb-2">Label the body parts</h2>
            {imageUrl ? (
                <div className="relative border border-gray-300 rounded-md overflow-hidden inline-block p-2">
                    <Stage width={IMAGE_WIDTH} height={IMAGE_HEIGHT}>
                        <Layer>
                            {loadedImage && (
                                <KonvaImage
                                    image={loadedImage}
                                    width={IMAGE_WIDTH}
                                    height={IMAGE_HEIGHT}
                                />
                            )}
                            {inputs.map((ann, index) => (
                                <Arrow
                                    key={index}
                                    points={[ann.endX, ann.endY, ann.startX, ann.startY]}
                                    stroke="black"
                                    strokeWidth={2}
                                    pointerLength={8}
                                    pointerWidth={8}
                                />
                            ))}
                        </Layer>
                    </Stage>

                    {inputs.map((ann, index) => (
                        <div
                            key={index}
                            className={`absolute w-20 h-6 flex items-center justify-between px-1 rounded-md text-sm shadow-lg text-center cursor-pointer border 
                            ${ann.value ? (ann.isCorrect ? "border-green-500 bg-green-100" : "border-red-500 bg-red-100") : "border-gray-400 bg-white"}`}
                            style={{ left: ann.endX - 40, top: ann.endY - 10 }}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => handleDrop(e, ann.id)}
                        >
                            {ann.value || "Drop Here"}
                            {ann.value && (
                                <XCircleIcon 
                                    className="w-4 h-4 text-gray-500 cursor-pointer"
                                    onClick={() => handleRemove(ann.id, ann.value)}
                                />
                            )}
                        </div>
                    ))}
                </div>
            ) : (
                <p className="text-gray-500">No image available for preview</p>
            )}

          
            <div className="mt-4 flex flex-wrap gap-3">
                {labels.map(label => (
                    <div
                        key={label.id}
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData("text/plain", label.text)}
                        className="bg-blue-500 text-white text-sm font-medium px-4 py-2 rounded-lg shadow-md cursor-pointer transition-all duration-200 
                        hover:bg-blue-600 hover:shadow-lg active:scale-95"
                    >
                        {label.text}
                    </div>
                ))}
            </div>
              {/* Feedback Message */}
              {isCorrect !== null && (
                <div
                    className={` py-3 px-4 rounded-lg text-center font-bold w-[600px] ${
                        isCorrect ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
                    }`}
                >
                    {isCorrect ? "✅ Correct! Well done!" : "❌ Incorrect. Try again!"}
                </div>
            )}

        </div>
    );
};

export default PreviewCategorySelector;



